import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, ChartBarIcon, EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const LearningPaths = () => {
    const [paths, setPaths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchPaths();
    }, []);

    const fetchPaths = async () => {
        try {
            const response = await api.get('/learning-paths');
            if (response.data.success) {
                setPaths(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching learning paths:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this learning path?')) {
            try {
                await api.delete(`/learning-paths/${id}`);
                setPaths(paths.filter(path => path.id !== id));
            } catch (error) {
                console.error('Error deleting learning path:', error);
            }
        }
    };

    const filteredPaths = paths.filter(path =>
        path.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        path.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatsData = () => {
        const totalPaths = paths.length;
        const totalEnrollments = paths.reduce((sum, path) => sum + parseInt(path.enrollment_count || 0), 0);
        const totalCompletions = paths.reduce((sum, path) => sum + parseInt(path.completion_count || 0), 0);
        const totalContents = paths.reduce((sum, path) => sum + parseInt(path.item_count || 0), 0);

        return [
            { title: 'Total Paths', value: totalPaths, icon: '🎯' },
            { title: 'Total Enrollments', value: totalEnrollments, icon: '👥' },
            { title: 'Completed', value: totalCompletions, icon: '✅' },
            { title: 'Total Contents', value: totalContents, icon: '📚' },
        ];
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3366]"></div>
            </div>
        );
    }

    return (
        <div className="relative z-10 p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF3366] to-[#FF6B4D]">
                            Learning Paths
                        </h1>
                        <p className="text-gray-400 mt-2">Create and manage learning paths for users</p>
                    </div>
                    <Link
                        to="/learning-paths/create"
                        className="px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white rounded-xl hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-all duration-200 font-medium shadow-lg transform hover:translate-y-[-2px]"
                    >
                        <PlusIcon className="h-5 w-5 mr-2 inline" />
                        Create New Path
                    </Link>
                </div>
            </div>

            {/* Search */}
            <div className="mb-8">
                <div className="relative max-w-md">
                    <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search learning paths..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full pl-10 px-4 py-3 bg-gradient-to-br from-[#151515] to-[#0A0A0A] border border-[#FF3366]/10 rounded-xl shadow-sm placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-[#FF3366] focus:border-transparent transition-all duration-200"
                    />
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                {getStatsData().map((stat, index) => (
                    <div key={index} className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] rounded-xl border border-[#FF3366]/10 p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="flex flex-col">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded-lg bg-black/20">
                                    <span className="text-[#FF3366] text-xl">{stat.icon}</span>
                                </div>
                                <h3 className="text-sm font-medium text-gray-300">{stat.title}</h3>
                            </div>
                            <div className="flex items-baseline">
                                <p className="text-3xl font-bold text-white">
                                    {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Learning Paths List */}
            <div className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] border border-[#FF3366]/10 shadow-lg rounded-xl overflow-hidden">
                <div className="p-6">
                    <h2 className="text-xl font-semibold text-white mb-4">Learning Paths</h2>
                    
                    {filteredPaths.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-6xl mb-4">🎯</div>
                            <h3 className="text-xl font-semibold text-white mb-2">No learning paths found</h3>
                            <p className="text-gray-400 mb-6">
                                {searchTerm ? `No paths found matching "${searchTerm}"` : 'Get started by creating a new learning path.'}
                            </p>
                            <Link
                                to="/learning-paths/create"
                                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white rounded-xl hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-all duration-200 font-medium"
                            >
                                <PlusIcon className="h-5 w-5 mr-2" />
                                Create Learning Path
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredPaths.map((path) => (
                                <div key={path.id} className="bg-[#0A0A0A] rounded-lg p-6 border border-[#FF3366]/5 hover:border-[#FF3366]/20 transition-all duration-300">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="flex-shrink-0 h-16 w-16">
                                                {path.image_url ? (
                                                    <img
                                                        className="h-16 w-16 rounded-lg object-cover"
                                                        src={path.image_url?.startsWith('http') ? path.image_url : `${process.env.REACT_APP_API_URL}${path.image_url}`}
                                                        alt={path.title}
                                                    />
                                                ) : (
                                                    <div className="h-16 w-16 rounded-lg bg-gradient-to-br from-[#FF3366]/20 to-[#FF6B4D]/20 flex items-center justify-center border border-[#FF3366]/20">
                                                        <span className="text-2xl font-bold text-[#FF3366]">
                                                            {path.title.charAt(0).toUpperCase()}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="text-lg font-semibold text-white">{path.title}</h3>
                                                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                                                        path.difficulty_level === 'beginner' 
                                                            ? 'bg-green-500/10 text-green-400 border border-green-400/30'
                                                            : path.difficulty_level === 'intermediate'
                                                            ? 'bg-[#FF6B4D]/10 text-[#FF6B4D] border border-[#FF6B4D]/30'
                                                            : 'bg-red-500/10 text-red-400 border border-red-400/30'
                                                    }`}>
                                                        {path.difficulty_level}
                                                    </span>
                                                </div>
                                                <p className="text-gray-400 text-sm mb-3">{path.description}</p>
                                                <div className="flex items-center gap-6 text-xs text-gray-500">
                                                    <span className="flex items-center gap-1">
                                                        <span className="text-[#FF3366]">⏱️</span>
                                                        {path.estimated_hours || 0} hours
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <span className="text-[#FF3366]">📚</span>
                                                        {path.item_count || 0} contents
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <span className="text-[#FF3366]">👥</span>
                                                        {path.enrollment_count || 0} enrolled
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <span className="text-[#FF3366]">✅</span>
                                                        {path.completion_count || 0} completed
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Link
                                                to={`/learning-paths/${path.id}/analytics`}
                                                className="p-2 text-gray-400 hover:text-[#FF3366] hover:bg-[#FF3366]/10 rounded-lg transition-all duration-200"
                                                title="View Analytics"
                                            >
                                                <ChartBarIcon className="h-5 w-5" />
                                            </Link>
                                            <Link
                                                to={`/learning-paths/${path.id}`}
                                                className="p-2 text-gray-400 hover:text-[#FF3366] hover:bg-[#FF3366]/10 rounded-lg transition-all duration-200"
                                                title="View Details"
                                            >
                                                <EyeIcon className="h-5 w-5" />
                                            </Link>
                                            <Link
                                                to={`/learning-paths/${path.slug || path.id}/edit`}
                                                className="p-2 text-gray-400 hover:text-green-400 hover:bg-green-400/10 rounded-lg transition-all duration-200"
                                                title="Edit"
                                            >
                                                <PencilIcon className="h-5 w-5" />
                                            </Link>
                                            <button
                                                onClick={() => handleDelete(path.id)}
                                                className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all duration-200"
                                                title="Delete"
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LearningPaths; 